import * as React from 'react';
import { BoostEligibilityInput, BoostEligibilitySelectionType } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { TOfferMember } from '@affiliates/components/MemberTable/types';
import { Select as RevfluenceSelect } from '@revfluence/fresh';
import { GearIcon } from '@revfluence/fresh-icons/solid';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/shadcn/components/ui/select';
import { Condition } from './BoostTypes';
import AdvancedSelectionForm from './AdvancedSelectionForm';

// Add custom styles for the select component
const selectStyles = `
  .ant-select-custom-height .ant-select-selector {
    height: 36px !important;
    min-height: 36px !important;
    padding: 4px 8px !important;
  }

  .ant-select-custom-height .ant-select-selection-item {
    line-height: 28px !important;
    height: 28px !important;
    padding: 0 !important;
    margin-bottom: 4px !important;
  }

  .ant-select-custom-height .ant-select-selection-placeholder {
    line-height: 28px !important;
    height: 28px !important;
  }

  .ant-select-custom-height .ant-select-selection-overflow {
    align-items: center !important;
    gap: 4px !important;
  }

  .ant-select-custom-height .ant-select-selection-overflow-item {
    line-height: 28px !important;
    margin-bottom: 4px !important;
  }

  .ant-select-dropdown {
    max-width: 100% !important;
    width: auto !important;
  }

  .ant-select-dropdown .ant-select-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px 12px !important;
    margin-bottom: 2px !important;
  }
`;

interface BoostEligibilityProps {
  boostEligibility: BoostEligibilityInput | null;
  onChange: (val: BoostEligibilityInput | null) => void;
  offerMembers: TOfferMember[];
  isAdvancedSelectionEnabled: boolean;
}

const ELIGIBILITY_OPTIONS = [
  { value: BoostEligibilitySelectionType.ALL, label: 'All members' },
  { value: BoostEligibilitySelectionType.INCLUDE, label: 'Specific Members' },
  { value: BoostEligibilitySelectionType.EXCLUDE, label: 'Excluding Members' },
];

// Hardcoded conditionOptions for now

export function BoostEligibility({ boostEligibility, onChange, offerMembers, isAdvancedSelectionEnabled }: BoostEligibilityProps) {
  const eligibilityType = boostEligibility?.eligibilityType || BoostEligibilitySelectionType.ALL;
  const selectedIds = boostEligibility?.memberIds || [];

  // Advanced selection state
  const [isAdvanced, setIsAdvanced] = React.useState(Boolean(boostEligibility?.advancedSelectionMetadata));
  const [advancedConditions, setAdvancedConditions] = React.useState<Condition[]>(boostEligibility?.advancedSelectionMetadata || []);
  const [advancedOperator, setAdvancedOperator] = React.useState<string>(boostEligibility?.advancedSelectionOperator || 'ALL');

  // Handler for gear toggle
  function handleToggleAdvanced() {
    setIsAdvanced((prev) => {
      const next = !prev;
      if (!next) {
        // Switching back to manual, clear advanced fields
        onChange({
          ...boostEligibility,
          advancedSelectionMetadata: null,
          advancedSelectionOperator: null,
        });
      } else {
        // Switching to advanced, clear manual fields
        onChange({
          ...boostEligibility,
          eligibilityType: null,
          memberIds: null,
          advancedSelectionMetadata: advancedConditions,
          advancedSelectionOperator: advancedOperator,
        });
      }
      return next;
    });
  }

  // Handler for operator change
  function handleOperatorChange(val: 'ALL' | 'OR') {
    setAdvancedOperator(val);
    if (isAdvanced) {
      onChange({
        ...boostEligibility,
        advancedSelectionMetadata: advancedConditions,
        advancedSelectionOperator: val,
      });
    }
  }

  // Handler for advanced conditions change
  function handleAdvancedConditionsChange(conds: Condition[]) {
    setAdvancedConditions(conds);
    if (isAdvanced) {
      onChange({
        ...boostEligibility,
        advancedSelectionMetadata: conds,
        advancedSelectionOperator: advancedOperator,
      });
    }
  }

  // Handler for manual selection change
  function handleEligibilityTypeChange(val: BoostEligibilitySelectionType) {
    if (val === BoostEligibilitySelectionType.ALL) {
      onChange({ eligibilityType: val, memberIds: null, id: boostEligibility?.id ? Number(boostEligibility?.id) : null });
    } else {
      onChange({ eligibilityType: val, memberIds: [], id: boostEligibility?.id ? Number(boostEligibility?.id) : null });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <style>{selectStyles}</style>
      {/* Inline eligibility type row */}
      <div className="flex items-center gap-2 text-sm justify-between">
        <div className="flex items-center gap-2">
          <span>Reward applies to</span>
          {!isAdvanced && (
            <Select value={eligibilityType} onValueChange={handleEligibilityTypeChange}>
              <SelectTrigger className="w-auto min-w-[120px] h-6 px-2 text-sm border-grey-3">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                {ELIGIBILITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-sm">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isAdvanced && (
            <span>Specific members</span>
          )}
          <span>in the offer</span>
        </div>
        {/* Always render the toggle and label in a fixed position */}
        {isAdvancedSelectionEnabled && (
          <div className="flex items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-pointer" onClick={handleToggleAdvanced}>
                  <GearIcon
                    aria-label="Toggle advanced selection"
                  />
                  <span className="text-xs text-grey-6 font-medium underline">{isAdvanced ? 'Advanced selection' : 'Manual selection'}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                {isAdvanced ? 'Switch to manual selection' : 'Switch to advanced selection'}
              </TooltipContent>
            </Tooltip>
          </div>)}
      </div>
      {/* Manual selection UI */}
      {!isAdvanced && (eligibilityType === BoostEligibilitySelectionType.INCLUDE || eligibilityType === BoostEligibilitySelectionType.EXCLUDE) && (
        <div className="flex flex-col gap-1 relative z-[50]">
          <RevfluenceSelect
            placeholder="Search and select members"
            mode="multiple"
            showSearch
            filterOption={(input, option) => String(option?.label).toLowerCase().includes(input.toLowerCase())}
            notFoundContent={offerMembers.length === 0 ? 'No members found' : null}
            allowClear
            dropdownStyle={{
              zIndex: 9999,
              pointerEvents: 'auto',
              userSelect: 'text',
              padding: '8px',
              maxWidth: '100%',
              width: 'auto'
            }}
            style={{ height: '36px' }}
            className="ant-select-custom-height"
            options={offerMembers.map((m) => ({ label: m.name, value: m.memberId }))}
            value={selectedIds}
            onChange={(ids) => onChange({
              ...boostEligibility,
              eligibilityType,
              memberIds: ids,
              id: boostEligibility?.id ?? null,
            })}
            open
          />
        </div>
      )}
      {/* Advanced selection UI */}
      {isAdvancedSelectionEnabled && isAdvanced && (
        <div className="flex flex-col gap-3 bg-white rounded-xl p-4 mt-2 border">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-xs text-gray-700">Member should follow</span>
            <Select value={advancedOperator} onValueChange={handleOperatorChange}>
              <SelectTrigger className="w-max border-0 font-semibold text-sm text-grey-5 focus:outline-none focus:ring-0 h-6 p-0 underline">
                <SelectValue placeholder="Select Condition Type" />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="ALL">all conditions</SelectItem>
                <SelectItem value="OR">any condition</SelectItem>
              </SelectContent>
            </Select>
            {/* <span className="font-normal text-xs text-gray-500">to be eligible</span> */}
          </div>
          <AdvancedSelectionForm
            value={advancedConditions}
            onChange={handleAdvancedConditionsChange}
          />
        </div>
      )}
    </div>
  );
}
